// @ts-strict-ignore
import React, {createContext} from 'react';

import {Translatable} from 'common/i18n';

export type IndustrySpec<T> = {
  display_name: T;
  default_events: Array<{
    name: T;
    properties: Array<{
      name: T;
      breakdowns: T[];
    }>;
  }>;
};

export type TranslatableIndustrySpec = IndustrySpec<Translatable>;
export type ResolvedIndustrySpec = IndustrySpec<string>;

export enum IndustryKey {
  B2B = `b2b`,
  Consumer = `consumer`,
  Social = `social`,
  Media = `media`,
  Gaming = `gaming`,
  Education = `education`,
  Healthcare = `healthcare`,
  Ecommerce = `ecommerce`,
  Agency = `agency`,
  Finance = `finance`,
  Marketplace = `marketplace`,
  Other = `other`,
}

export enum ExampleEventKey {
  SignUp = `sign-up`,
  PageView = `page-view`,
  RequestDemo = `request-demo`,
  CreateItem = `create-item`,
  Engage = `engage`,
  Share = `share`,
  ContentView = `content-view`,
  PurchaseMedia = `purchase-media`,
  PurchaseEcommerce = `purchase-ecommerce`,
  StartLevel = `start-level`,
  FinishLevel = `finish-level`,
  StartCourse = `start-course`,
  FinishModule = `finish-module`,
  FinishCourse = `finish-course`,
  CompleteSearch = `complete-search`,
  NewAppointment = `new-appointment`,
  ViewItem = `view-item`,
  AddtoCart = `add-to-cart`,
  ViewCart = `view-cart`,
  SelectBank = `select-bank`,
  FundedAccount = `funded-account`,
  Transaction = `transaction`,
}

export enum Step {
  Implement = `implement`,
  GetStarted = `get-started`,
  Connect = `connect`,
  Verify = `verify`,
}

export type CompleteStep = {
  [key in Step]?: boolean;
};

export type CheckIntegrationResult = {is_integrated: boolean};

export const SetupGuideContext = createContext<{
  modalManager: {
    openModal: () => void;
    closeModal: () => void;
  };
  step: Step;
  setStep: React.Dispatch<React.SetStateAction<Step>>;
  setupGuideInfo: SetupGuideType;
  setSetupGuideInfo: React.Dispatch<React.SetStateAction<SetupGuideType>>;
  isIntegrated: boolean;
  setIsIntegrated: React.Dispatch<React.SetStateAction<boolean>>;
  connectWithSDK: boolean;
  setConnectWithSDK: React.Dispatch<React.SetStateAction<boolean>>;
  selectAltSDK: boolean;
  setSelectAltSDK: React.Dispatch<React.SetStateAction<boolean>>;
  useAutocapture: boolean;
  setUseAutocapture: React.Dispatch<React.SetStateAction<boolean>>;
  useSessionReplay: boolean;
  setUseSessionReplay: React.Dispatch<React.SetStateAction<boolean>>;
  completed: CompleteStep;
}>(null);

export const VisibilityContext = createContext<{
  visibility: boolean;
  setVisibility: React.Dispatch<React.SetStateAction<boolean>>;
}>(null);

// Utility type
type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

type ImmersiveOnboardingRuntime = {
  [K in keyof SetupGuideResponse as SnakeToCamelCase<K>]: SetupGuideResponse[K];
};

// This is version-agnostic runtime type
export type SetupGuideType = Omit<ImmersiveOnboardingRuntime, `industry` | `stepsCompleted`> & {
  industry: IndustryKey;
  stepsCompleted: Set<Step>;
};

// API Response Type with primitive types
export type SetupGuideResponse = {
  author: string;
  email: string;
  events: string[];
  industry: string;
  modified: string;
  project_id: number;
  project_token: string;
  steps_completed: string[];
  events_properties_map?: {
    [event: string]: string[];
  };
};

export enum SubStep {
  Welcome = `welcome`,
  EventConfirmation = `event`,
  Segmentation = `property`,
  ReadyToConnect = `ready`,
}

export const UserEventPropertiesMapContext = createContext<{
  eventKeys: string[];
  setEventKeys: React.Dispatch<React.SetStateAction<string[]>>;
  currentEventPropertiesMap: Map<string, string[]>;
  setCurrentEventPropertiesMap: React.Dispatch<React.SetStateAction<Map<string, string[]>>>;
}>(null);

export type ProjectTallies = {
  hasEvents: boolean;
  hasUsers: boolean;
  hasSessionRecordings: boolean;
};
